import { useState } from "react";
import GoldButton from "../components/ui/GoldButton";
import { useToast } from "../hooks/use-toast";
import { createClientRequestId, saveClientRequest } from "../lib/clientRequests";
import ScrollReveal from "../components/motion/ScrollReveal";
import BackgroundGradient from "../components/effects/BackgroundGradient";
import FloatingShapes from "../components/graphics/FloatingShapes";
import { supabase } from "../lib/supabase";

const services = ["Website", "CRM", "ERP", "AI Agent", "AI Call Centre", "SEO/Marketing", "Other"];
const budgets = ["₹25K–1L", "₹1L–5L", "₹5L–20L", "₹20L+", "Not sure yet"];
const timelines = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "#0E0E0E",
  border: "1px solid rgba(212,168,67,0.2)",
  borderRadius: 3,
  padding: "14px 18px",
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 300,
  fontSize: 14,
  color: "#F8F4EC",
  outline: "none",
  transition: "border-color 0.2s",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.2em",
  color: "#908A83",
  textTransform: "uppercase",
  display: "block",
  marginBottom: 8,
};

const Contact = () => {
  const { toast } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    description: "",
  });
  const [errors, setErrors] = useState<{ name?: string; email?: string; description?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const toggle = (service: string) => {
    setSelected((prev) => (prev.includes(service) ? prev.filter((item) => item !== service) : [...prev, service]));
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.currentTarget;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "name" || name === "email" || name === "description") {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const nextErrors: { name?: string; email?: string; description?: string } = {};

    if (!form.name.trim()) {
      nextErrors.name = "Please add your full name.";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Please add your email address.";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!form.description.trim()) {
      nextErrors.description = "Please describe your project.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      toast({
        title: "Check the form",
        description: "A few required details are missing or invalid.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        message: `Services: ${selected.join(', ')}\nBudget: ${budget}\nTimeline: ${timeline}\n\n${form.description.trim()}`,
        source: "contact-form",
        source_page: window.location.pathname,
      };

      const { error: submitError } = await supabase.from('contact_submissions').insert([payload]);
      
      if (submitError) throw new Error(submitError.message);

      // Optional: Wait for track lead analytics
      try {
        const { trackLead } = await import("../lib/analytics");
        trackLead({ email: payload.email, phone: payload.phone, method: 'Contact Form' });
      } catch (err) {
        console.error("Analytics error:", err);
      }

      setForm({ name: "", company: "", email: "", phone: "", description: "" });
      setSelected([]);
      setBudget("");
      setTimeline("");
      setErrors({});

      toast({
        title: "Enquiry sent",
        description: "Thanks. We’ll review it and get back to you shortly.",
      });
    } catch {
      toast({
        title: "Submission failed",
        description: "We couldn’t save your enquiry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section style={{ background: "#050505", minHeight: "100vh", paddingTop: 120, position: "relative", overflow: "hidden" }}>
        <BackgroundGradient />
        <FloatingShapes seed={77} count={5} />
        <div className="owl-contact-grid" style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 40px", display: "grid", gridTemplateColumns: "40% 60%", gap: 80, alignItems: "start", position: "relative", zIndex: 2 }}>
          <ScrollReveal direction="left">
          <div className="owl-contact-sidebar" style={{ position: "sticky", top: 120 }}>
            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 300,
                fontSize: "clamp(48px, 5vw, 72px)",
                color: "#F8F4EC",
                lineHeight: 0.88,
                marginBottom: 12,
              }}
            >
              Let&apos;s build
            </div>
            <div
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(48px, 5vw, 72px)",
                color: "#D4A843",
                lineHeight: 0.95,
                marginBottom: 40,
              }}
            >
              something.
            </div>

            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 16,
                color: "#908A83",
                lineHeight: 1.7,
                marginBottom: 40,
                maxWidth: 360,
              }}
            >
              Tell us about your project and we&apos;ll get back within 24 hours — with a plan, not a sales pitch.
            </p>

            {[
              { label: "Location", value: "Headquartered in New Delhi, India" },
              { label: "Email", value: "obsidianweblabs@gmail.com" },
              { label: "Reach", value: "International project delivery" },
            ].map((item) => (
              <div key={item.label} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    color: "#D4A843",
                    marginBottom: 2,
                    textTransform: "uppercase",
                  }}
                >
                  {item.label}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 14, color: "#F8F4EC" }}>{item.value}</div>
              </div>
            ))}

            <div
              style={{
                marginTop: 40,
                background: "rgba(212,168,67,0.08)",
                border: "1px solid rgba(212,168,67,0.2)",
                borderRadius: 3,
                padding: "16px 20px",
              }}
            >
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  color: "#D4A843",
                  marginBottom: 4,
                }}
              >
                OUR PROMISE
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 13, color: "#F8F4EC" }}>
                We reply within 24 hours. Always.
              </div>
            </div>

            <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 10 }}>
              {["500+ Projects delivered", "NDA available on request", "Free initial consultation", "No lock-in contracts"].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#D4A843", fontSize: 12 }}>+</span>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 13, color: "#908A83" }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          </ScrollReveal>

          <ScrollReveal direction="right" delay={0.2}>
          <form
            className="owl-contact-form"
            onSubmit={handleSubmit}
            style={{ background: "#0E0E0E", border: "1px solid rgba(212,168,67,0.15)", borderRadius: 4, padding: 48 }}
          >
            <div className="owl-contact-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div>
                <label htmlFor="contact-name" style={labelStyle}>Full Name *</label>
                <input
                  id="contact-name"
                  name="name"
                  value={form.name}
                  onChange={handleFieldChange}
                  style={{ ...inputStyle, borderColor: errors.name ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)" }}
                  placeholder="Your full name"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.6)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.name ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)";
                  }}
                />
                {errors.name && <p style={{ color: "#D85B5B", fontSize: 12, marginTop: 8 }}>{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="contact-company" style={labelStyle}>Company</label>
                <input
                  id="contact-company"
                  name="company"
                  value={form.company}
                  onChange={handleFieldChange}
                  style={inputStyle}
                  placeholder="Your company"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.6)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.2)";
                  }}
                />
              </div>
            </div>

            <div className="owl-contact-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div>
                <label htmlFor="contact-email" style={labelStyle}>Email *</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFieldChange}
                  style={{ ...inputStyle, borderColor: errors.email ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)" }}
                  placeholder="your@email.com"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.6)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.email ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)";
                  }}
                />
                {errors.email && <p style={{ color: "#D85B5B", fontSize: 12, marginTop: 8 }}>{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="contact-phone" style={labelStyle}>Phone / WhatsApp</label>
                <input
                  id="contact-phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleFieldChange}
                  style={inputStyle}
                  placeholder="+91 XXXXX XXXXX"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.6)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(212,168,67,0.2)";
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>What do you need?</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {services.map((service) => {
                  const active = selected.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggle(service)}
                      style={{
                        background: active ? "#D4A843" : "transparent",
                        border: `1px solid ${active ? "#D4A843" : "rgba(212,168,67,0.3)"}`,
                        color: active ? "#050505" : "#908A83",
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        padding: "8px 16px",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="owl-contact-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div>
                <label htmlFor="contact-budget" style={labelStyle}>Budget</label>
                <select
                  id="contact-budget"
                  value={budget}
                  onChange={(e) => setBudget(e.currentTarget.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}
                >
                  <option value="">Select budget range</option>
                  {budgets.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-timeline" style={labelStyle}>Timeline</label>
                <select
                  id="contact-timeline"
                  value={timeline}
                  onChange={(e) => setTimeline(e.currentTarget.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}
                >
                  <option value="">Select timeline</option>
                  {timelines.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 32 }}>
              <label htmlFor="contact-description" style={labelStyle}>Project Description</label>
              <textarea
                id="contact-description"
                name="description"
                rows={5}
                value={form.description}
                onChange={handleFieldChange}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  lineHeight: 1.6,
                  borderColor: errors.description ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)",
                }}
                placeholder="Tell us about what you're building, what problem it solves, and any specific requirements..."
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "rgba(212,168,67,0.6)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = errors.description ? "rgba(216,91,91,0.7)" : "rgba(212,168,67,0.2)";
                }}
              />
              {errors.description && <p style={{ color: "#D85B5B", fontSize: 12, marginTop: 8 }}>{errors.description}</p>}
            </div>

            <GoldButton fullWidth size="lg" type="submit" disabled={submitting}>
              {submitting ? "SENDING..." : "SEND MESSAGE →"}
            </GoldButton>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 11, color: "#908A83", textAlign: "center", marginTop: 16 }}>
              We don&apos;t share your data with anyone — ever.
            </p>
          </form>
          </ScrollReveal>
        </div>
      </section>

      <style>{`
        @media (max-width: 960px) {
          .owl-contact-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
            padding: 72px 24px !important;
          }

          .owl-contact-sidebar {
            position: static !important;
          }
        }

        @media (max-width: 640px) {
          .owl-contact-two-col {
            grid-template-columns: 1fr !important;
          }

          .owl-contact-form {
            padding: clamp(20px, 5vw, 32px) !important;
          }
        }
      `}</style>
    </>
  );
};

export default Contact;
